// @ts-ignore
import { useEffect } from "react";

import { useIntl } from "react-intl";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";
import {
    AnchorPageRelayQuery,
    shapeAnchorContentfulResponse
} from "src/contentful";

import { anchorPageRelayQuery } from "@/__generated_contentful__/anchorPageRelayQuery.graphql";
import { CarouselProps } from "@/components/confluence/carousel/src";
import { useLogMeInD0to6 } from "@/components/confluence/hooks/use-log-me-in-d0to6.tsx";
import { PaidHeroCarousel } from "@/patterns/paid-hero/src/paid-hero-carousel";
import HeroSection from "@/templates/software/confluence/confluence-software-page/src/sections/log-me-in/hero";
import {
    AnchorContentsResponse,
    PaidLandingPersonaTypes
} from "@/utils/anchor-queries/get-anchor-contents";
import { experiments, useExperimentExposure } from "@/utils/experiment";
import { FeatureExposed } from "@/utils/feature-exposed";

import { ConfluenceSoftwarePageProps } from "./confluence-software-page";
import { carouselHeroMessages } from "./messages";
import { ConfluencePagePaidLpConflAnchor } from "./paid-lp-confl-anchor.tsx";
import { isTargetedSubAnchor } from "./subAnchors/subAnchor-util.tsx";

export interface ConfluencePageAnchorLpConfCompetitorsRelayProps
    extends ConfluenceSoftwarePageProps {
    anchorContents?: AnchorContentsResponse | null;
    persona?: string;
    queryRef?: PreloadedQuery<anchorPageRelayQuery>;
}
export const ConfluenceSoftwarePageAnchorCompetitors = (
    pageProps: ConfluencePageAnchorLpConfCompetitorsRelayProps
) => {
    const intl = useIntl();
    const [exposureSubAnchorFired, fireSubAnchorExposure] =
        useExperimentExposure(experiments.cfind4782LandingPageSubAnchorV3);
    useEffect(() => {
        if (
            isTargetedSubAnchor(pageProps.subAnchor) &&
            !exposureSubAnchorFired
        ) {
            fireSubAnchorExposure();
        }
    }, [exposureSubAnchorFired, fireSubAnchorExposure]);

    const data = usePreloadedQuery(
        AnchorPageRelayQuery,
        pageProps.queryRef ||
            (null as unknown as PreloadedQuery<anchorPageRelayQuery>)
    );
    pageProps = Object.assign({}, pageProps, {
        anchorContents: shapeAnchorContentfulResponse(data)
    });

    const { userProfileName, recentSites, shouldShowLogMeIn } = useLogMeInD0to6(
        {
            availableProducts: pageProps.availableProducts,
            userProfile: pageProps.userProfile
        }
    );

    const carouselSlides = [
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Competitors.achieve-team-goals-together.title",
                defaultMessage: "Achieve team goals together"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Competitors.achieve-team-goals-together.body",
                defaultMessage:
                    "Plan projects, take notes, and brainstorm. Let AI write your first draft."
            }),
            imgSrc: "https://dam-cdn.atl.orangelogic.com/AssetLink/1e81unagi717g13fj44qtgpk0h8r0537.webp",
            imgAltText: "A Confluence page being edited"
        },
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Competitors.get-everyone-on-the-same-page.title",
                defaultMessage: "Get everyone on the same page"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Competitors.get-everyone-on-the-same-page.body",
                defaultMessage:
                    "Collaborate with your teams using real-time editing and inline comments."
            }),
            imgSrc: "https://dam-cdn.atl.orangelogic.com/AssetLink/i0011ky0775062fk18784clr4dcxd5wg.webp",
            imgAltText:
                "A Confluence page with multiple teammates editing and leaving comments"
        },
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Competitors.connect-teams-content-context.title",
                defaultMessage: "Connect teams, content, and context"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Competitors.connect-teams-content-context.body",
                defaultMessage:
                    "Stay organized in dedicated workspaces, equipped with AI-powered search."
            }),
            imgSrc: "https://dam-cdn.atl.orangelogic.com/AssetLink/fpd20037c363n21cfmh45nc5m50co161.webp",
            imgAltText: "Organized workspace in Confluence"
        }
    ];

    const carouselHeroProps: CarouselProps = {
        "aria-label": intl.formatMessage(carouselHeroMessages.ariaLabel),
        "id": "confluence-competitors-carousel",
        "slides": carouselSlides
    };

    const pageId = "anchor-lp-conf-competitors";

    const isLoggedIn = !!userProfileName;
    let customHeroSection;

    const getLiftShiftHeroSectionBrandUpdate = () => {
        return (
            <PaidHeroCarousel
                pageId={pageId}
                embeddedSignup={{ bundle: "confluence" }}
                carouselSlides={carouselSlides}
                shouldUseNewBrandBackground
                shouldUseTransparentBackground
            />
        );
    };

    if (isLoggedIn) {
        if (shouldShowLogMeIn) {
            customHeroSection = (
                <FeatureExposed
                    experimentKey="cfind-2801-get-paid-users-back-to-product"
                    group="Test"
                >
                    <HeroSection
                        confluenceSites={recentSites}
                        userProfileName={userProfileName}
                        hideExploreMore
                        loggedInPaid
                    />
                </FeatureExposed>
            );
        } else {
            customHeroSection = getLiftShiftHeroSectionBrandUpdate();
        }
    } else {
        // Logged out user
        customHeroSection = null;
    }

    return (
        <FeatureExposed
            experimentKey="cfind-3090-paid-confluence-product-tour"
            group="Test"
        >
            <ConfluencePagePaidLpConflAnchor
                {...pageProps}
                pageId={pageId}
                persona={"unknown" as PaidLandingPersonaTypes}
                pageVariant="brandUpdate"
                dataProductKey="confluence-PaidLpConflCompetitors"
                keyword={pageProps.keyword}
                carouselHeroProps={carouselHeroProps}
                customHeroSection={customHeroSection}
            />
        </FeatureExposed>
    );
};
